import { motion } from 'framer-motion';
import { MapPin, Phone, MessageSquare, Train, Clock, AlertCircle } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/utils/translations';

const SCHEDULE_HOURS = [
  '09:30 - 13:30 | 15:30 - 19:30',
  '09:30 - 13:30 | 15:30 - 19:30',
  '09:30 - 13:30 | 15:30 - 19:30',
  '09:30 - 13:30 | 15:30 - 19:30',
  '09:30 - 13:30 | 15:30 - 19:00',
];

export default function ContactSchedule() {
  const { language } = useLanguage();
  const ts = translations[language].schedule;
  const tc = translations[language].contact;

  return (
    <section id="contact" data-testid="contact-section">
      <div className="py-20 md:py-32 bg-[#e3e6e8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770] mb-4">{ts.title}</h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.5 }}
            className="max-w-2xl mx-auto bg-white rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm"
            data-testid="schedule-table"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-10 bg-[#fff6e5] rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-[#ffa600]" />
              </div>
              <h3 className="font-semibold text-[#5b6770]">{ts.title}</h3>
            </div>
            <div className="space-y-3">
              {ts.days.map((day, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0" data-testid={`schedule-row-${i}`}>
                  <span className="text-sm font-medium text-[#5b6770]">{day}</span>
                  <span className="text-sm text-[#5b6770]">{SCHEDULE_HOURS[i]}</span>
                </div>
              ))}
            </div>
            <div className="mt-5 pt-4 border-t border-gray-100 flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-[#ffa600] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium text-[#5b6770]">{ts.emergency}</p>
                <p className="text-xs text-[#5b6770]">{ts.emergencyNote}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="py-20 md:py-32 bg-[#e3e6e8]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-80px' }}
            transition={{ duration: 0.6 }}
            className="text-center mb-14"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-[#5b6770]">{tc.title}</h2>
          </motion.div>

          <div className="grid lg:grid-cols-2 gap-10">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5 }}
              className="space-y-5"
            >
              <ContactItem icon={MapPin} title={tc.address}>
                <p className="text-[#5b6770] text-sm">Carrer de Balmes, 421, 1º B<br />08022 Barcelona<br />{"Sarrià - Sant Gervasi"}</p>
              </ContactItem>
              <ContactItem icon={Phone} title={tc.phone}>
                <a href="tel:+34932121044" className="text-[#5b6770] text-sm hover:text-[#ffa600] transition-colors" data-testid="contact-phone-link">
                  932 12 10 44
                </a>
              </ContactItem>
              <ContactItem icon={MessageSquare} title={tc.whatsapp}>
                <p className="text-[#5b6770] text-sm">{tc.whatsappDesc}</p>
              </ContactItem>
              <ContactItem icon={Train} title={tc.transport}>
                <p className="text-[#5b6770] text-sm">{tc.transportLine1}<br />{tc.transportLine2}</p>
              </ContactItem>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: '-40px' }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="rounded-2xl overflow-hidden shadow-[0_8px_30px_rgba(0,0,0,0.06)] border border-gray-100 min-h-[350px]"
            >
              <iframe
                src="https://maps.google.com/maps?q=Carrer+de+Balmes+421+08022+Barcelona&t=&z=16&ie=UTF8&iwloc=&output=embed"
                title="Clínica Dental Brandt-Lafuente Location"
                className="w-full h-full min-h-[350px]"
                frameBorder="0"
                allowFullScreen
                loading="lazy"
                data-testid="contact-map"
              />
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactItem({ icon: Icon, title, children }) {
  return (
    <div className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors">
      <div className="w-10 h-10 bg-[#fff6e5] rounded-xl flex items-center justify-center shrink-0">
        <Icon className="w-5 h-5 text-[#ffa600]" />
      </div>
      <div>
        <h4 className="text-sm font-semibold text-[#5b6770] mb-1">{title}</h4>
        {children}
      </div>
    </div>
  );
}
